import React, {Fragment, MouseEvent, useContext, useEffect, useState} from 'react';
import {ICONS} from 'patient-ping-remedy/packages/icon';
import {AdditionalDetailsLabel, CustomIconButton, Wrapper} from '@clients/profile_page/ClientDemographicsEdit.styles';
import Spacer from "patient-ping-remedy/packages/spacer";
import {colors, sizes} from "patient-ping-remedy/packages/theme";
import {css} from "@emotion/css";
import {useActiveRoster} from "@/store/roster_store";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {Form, RosterFormCodeBinding} from "@/api/dto/forms";
import RosterBindingFormDrawer from "@common/RosterBindingFormDrawer";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import {StandardTypography} from "patient-ping-remedy/packages/typography";
import Modal from "patient-ping-remedy/packages/modal";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {AxiosError} from "axios";
import Helpers from "@/helpers/helpers";
import {GENERICERROR} from "@manual_encounters/constants/BannerMessages";
import {useAlertStore} from "@/store/alert_store";
import {useLocation, useSearchParams} from "react-router-dom";
import {SearchParam} from "@/helpers/search_params";
import {useMixpanel} from "@/hooks/useMixpanel";
import {MixpanelEventType, MixpanelFormOpenedEvent} from "@/api/dto/mixpanel";

type Props = {
  onClick?(): void;
  isEditAvailable?: boolean;
  patientId: string;
}

export enum PatientEditTypes {
  DEMOGRAPHICS = 'CLIENT_DEMOGRAPHICS',
  ADDITIONAL_DETAILS = 'CLIENT_ADDITIONAL_DETAILS',
}

export default function ClientDemographicsEdit(props: Props) {
  const currentRoster = useActiveRoster();
  const {carecoApi} = useContext(CarecoApiContext);
  const {addAlert} = useAlertStore();
  const queryClient = useQueryClient();
  const location = useLocation();
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();

  const [isAdditionalDetailsOpen, setIsAdditionalDetailsOpen] = useState(false);
  const [refreshPromptForm, setRefreshPromptForm] = useState<Form | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const editing = searchParams.get(SearchParam.EDIT_PATIENT);

  const {data: form, isLoading : isFormLoading} = useQuery({
    queryFn: async () => carecoApi?.getFormByRosterCodeBindingAndPatientId(
      RosterFormCodeBinding.CLIENT_ADDITIONAL_DETAILS, props.patientId, currentRoster?.id ?? 0),
    queryKey: ['form', props.patientId, currentRoster?.id, RosterFormCodeBinding.CLIENT_ADDITIONAL_DETAILS],
    enabled: !!carecoApi && !!currentRoster
  });

  const {data: formTemplate, isLoading : isTemplateLoading } = useQuery({
    queryFn: async () =>
      carecoApi?.getFormTemplateByRosterCodeBinding(RosterFormCodeBinding.CLIENT_ADDITIONAL_DETAILS, currentRoster?.id ?? 0),
    queryKey: ['formTemplate', RosterFormCodeBinding.CLIENT_ADDITIONAL_DETAILS, currentRoster?.id],
    enabled: !!carecoApi && !!currentRoster && !isFormLoading && !form,
  });

  const additionalDetailsFormTemplate = form && form.form_template ? form.form_template : formTemplate;
  const isLoading = isTemplateLoading || isFormLoading;

  const refreshFormMutation = useMutation({
    mutationFn: async (form: Form) => {
      return carecoApi?.refreshTemplateFormToLatestVersion(
        form.client.roster.id,
        form.client.publicPatientId,
        form.id,
      );
    },
    onSuccess: () => {
      addAlert({
        content: 'Form refreshed to latest version successfully.',
        type: ToastType.SUCCESS,
      });
      void queryClient.invalidateQueries({
        queryKey: ['form', props.patientId, currentRoster!.id, RosterFormCodeBinding.CLIENT_ADDITIONAL_DETAILS]
      });
      setIsAdditionalDetailsOpen(true);
      setRefreshPromptForm(null);
    },
    onError: (error: AxiosError, form: Form) => {
      setRefreshPromptForm(form);
      Helpers.alertOnError(`Failed to refresh form. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  useEffect(() => {
    if(editing === PatientEditTypes.ADDITIONAL_DETAILS) {
      if(isFormLoading) {
        return;
      }

      if (form && form.form_template && form.form_template_version &&
          (!!form.form_template.latestVersion && form.form_template_version < form.form_template.latestVersion
              || (additionalDetailsFormTemplate && form.form_template.id != additionalDetailsFormTemplate.id))) {
        setRefreshPromptForm(form);
        return;
      }

      setIsAdditionalDetailsOpen(true);
    } else {
      setIsAdditionalDetailsOpen(false);
    }

    if(!Object.values(PatientEditTypes).includes(editing as PatientEditTypes)) {
      searchParams.delete(SearchParam.EDIT_PATIENT);
      setSearchParams(searchParams);
    }
  }, [editing, form, additionalDetailsFormTemplate]);

  useEffect(() => {
    if(isAdditionalDetailsOpen && !isLoading && !additionalDetailsFormTemplate) {
      searchParams.delete(SearchParam.EDIT_PATIENT);
      setSearchParams(searchParams);
      setIsAdditionalDetailsOpen(false);
    }
  }, [isAdditionalDetailsOpen, additionalDetailsFormTemplate, isLoading]);

  const handleDemographicEditClick = (event: MouseEvent) => {
    event.stopPropagation();
    props.onClick?.();
  };

  const canShowAdditionalDetailsFormTemplate = !isFormLoading && !isTemplateLoading && !!additionalDetailsFormTemplate
    && (location.pathname.includes('clients/profile') || location.pathname.includes('tasks/details'));

  return (
    <Wrapper onClick={(e) => e.stopPropagation()}>
      {props.isEditAvailable &&
        <Fragment>
          <CustomIconButton
            data-testid="client-demographics-edit"
            className="client-demographics-edit"
            iconClass={ICONS.pencil}
            onClick={handleDemographicEditClick}
            overflowBackground
          />
          {canShowAdditionalDetailsFormTemplate &&
            <Fragment>
              <Spacer itemWidth={sizes.small} />
              <div className={css({borderLeft: `1px solid ${colors.gray3}`, width: sizes.small})}/>
            </Fragment>
          }
        </Fragment>
      }

      {canShowAdditionalDetailsFormTemplate && currentRoster &&
        <Fragment>
          <AdditionalDetailsLabel
            onClick={() => {
              if (mixpanelEventProperties && currentRoster) {
                const event: MixpanelFormOpenedEvent = {
                  eventName: MixpanelEventType.BIH_ADDITIONAL_DETAILS_FORM_OPENED,
                  clientId: props.patientId,
                  codeBinding: RosterFormCodeBinding.CLIENT_ADDITIONAL_DETAILS,
                  ...mixpanelEventProperties,
                  ...getCurrentGroupDetails(currentRoster)
                };
                carecoApi?.postMixpanelEvent(event);
              }
              searchParams.set(SearchParam.EDIT_PATIENT, PatientEditTypes.ADDITIONAL_DETAILS);
              setSearchParams(searchParams);
            }}
          >
            Additional details
          </AdditionalDetailsLabel>

          <div>
            <RosterBindingFormDrawer
              rosterCodeBinding={RosterFormCodeBinding.CLIENT_ADDITIONAL_DETAILS}
              formTemplate={additionalDetailsFormTemplate}
              close={() => {
                setIsAdditionalDetailsOpen(false);
                searchParams.delete(SearchParam.EDIT_PATIENT);
                setSearchParams(searchParams);
              }}
              open={isAdditionalDetailsOpen}
              patientId={props.patientId}
              form={form}
            />
          </div>
          <div>
            <Modal
              headerText={'A newer form version is available'}
              isOpen={!!refreshPromptForm}
              hideModal={() => setRefreshPromptForm(null)}
              buttons={[
                <Button
                  key={'keep-current-version-button'}
                  styleType={StyleType.TERTIARY}
                  onClick={() => {
                    setIsAdditionalDetailsOpen(true);
                    setRefreshPromptForm(null);
                  }}
                  disabled={refreshFormMutation.isPending}
                >
                    Keep current version
                </Button>,
                <Button
                  key={'use-latest-version-button'}
                  onClick={() => {
                    if (!form) return;
                    refreshFormMutation.mutate(form);
                  }}
                  disabled={refreshFormMutation.isPending}
                >
                    Use latest version
                </Button>
              ]}
            >
              <StandardTypography>
                This in-progress form was started on an older version.
              </StandardTypography>
            </Modal>
          </div>
        </Fragment>
      }
    </Wrapper>
  );
}
